import React, { useEffect, useState, useMemo, useRef } from 'react';

import {
  Card,
  Stack,
  Typography,
  Button,
  Divider,
  FormControl,
  TextField,
  Link,
  Modal,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Select,
  MenuItem,
  OutlinedInput,
  ListSubheader,
  Checkbox,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import DynamicTable from 'src/components/shared/DynamicTable';
import CustomFilter from 'src/components/filter/CustomFilter';
import FilterButton from 'src/components/shared/FilterButton';
import ClearButton from 'src/components/shared/ClearButton';
import FilterViewButton from 'src/components/shared/FilterViewButton';
import api from 'src/services/BaseService';
import { useLocalization } from 'src/context/LocalizationContext';
import { DatePicker } from '@mui/x-date-pickers';
import { tokenService } from 'src/services';
import { useNavigate } from 'react-router-dom';
import DescriptionIcon from '@mui/icons-material/Description';
import Services from 'src/services/Services';
import CloseIcon from '@mui/icons-material/Close';

const formatters = {
  toDaTeString: (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(Number(timestamp) * 1000);
    return date
      .toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
      .replace(/\./g, '/');
  },
  toTimeString: (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(Number(timestamp) * 1000);
    return date.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  },
  toDateTimeString: (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(Number(timestamp) * 1000);
    const dStr = date
      .toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
      .replace(/\./g, '-');
    const tStr = date.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${dStr} ${tStr}`;
  },
};

const MAX_VISIBLE_CHIPS = 2;

const RightHeaderSummary = ({ items }) => {
  const [overflowOpen, setOverflowOpen] = useState(false);

  if (!Array.isArray(items) || items.length === 0) return null;

  const visibleItems = items.slice(0, MAX_VISIBLE_CHIPS);
  const hiddenCount = items.length - MAX_VISIBLE_CHIPS;

  const chipLabel = (label, value) => (
    <>
      <span
        title={label}
        style={{
          maxWidth: 170,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}:
      </span>
      <b>{value}</b>
    </>
  );

  const chipSx = {
    width: 'max-content',
    maxWidth: 260,
    '& .MuiChip-label': { display: 'flex', alignItems: 'center', gap: 0.75 },
  };

  return (
    <>
      <Box
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}
      >
        {visibleItems.map((it, idx) => {
          const label =
            String(it?.label ?? it?.key ?? '').trim() || 'Bilinmeyen';
          const value = it?.value ?? 0;
          return (
            <Chip
              key={it?.key ?? idx}
              variant="outlined"
              size="small"
              sx={chipSx}
              label={chipLabel(label, value)}
            />
          );
        })}
        {hiddenCount > 0 && (
          <Chip
            label={`+${hiddenCount}`}
            size="small"
            variant="outlined"
            onClick={() => setOverflowOpen(true)}
            sx={{ cursor: 'pointer', width: 'max-content' }}
          />
        )}
      </Box>

      <Modal open={overflowOpen} onClose={() => setOverflowOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            minWidth: 280,
            maxWidth: 400,
            maxHeight: '80vh',
            overflowY: 'auto',
            outline: 0,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, lineHeight: 1.2 }}
              >
                Araç Özeti
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {items.length} araç tipi
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setOverflowOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Stack spacing={1}>
            {items.map((it, idx) => {
              const label =
                String(it?.label ?? it?.key ?? '').trim() || 'Bilinmeyen';
              const value = it?.value ?? 0;
              return (
                <Box
                  key={it?.key ?? idx}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 2,
                    py: 1,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: 'text.primary' }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: 'primary.main',
                      minWidth: 28,
                      textAlign: 'right',
                    }}
                  >
                    {value}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Modal>
    </>
  );
};

const parsePriceString = (priceStr) => {
  if (priceStr == null) return 0;

  let s = String(priceStr)
    .trim()
    .replace(/[^\d.,-]/g, '');

  if (!s) return 0;

  const hasDot = s.includes('.');
  const hasComma = s.includes(',');

  if (hasDot && hasComma) {
    s = s.replace(/\./g, '').replace(/,/g, '.');
  } else if (hasComma && !hasDot) {
    s = s.replace(/,/g, '.');
  } else if (hasDot && !hasComma) {
    const lastDot = s.lastIndexOf('.');
    const decimals = s.length - lastDot - 1;

    if (decimals === 2) {
    } else {
      s = s.replace(/\./g, '');
    }
  }

  const parsed = parseFloat(s);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatPriceString = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '0,00';
  return value.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const maskNamePart = (str) => {
  if (!str) return '';
  const raw = String(str).trim();
  const visible = 2;
  const maxLen = 5;
  const length = Math.min(raw.length, maxLen);
  if (length <= visible) return raw.slice(0, length);
  const maskedLen = length - visible;
  return raw.slice(0, visible) + '*'.repeat(maskedLen);
};

const findPriceEntry = (priceList = [], toolName = '') => {
  if (!toolName) return null;
  const tn = String(toolName).trim().toLowerCase();
  if (tn === 'ring' || tn.includes('koli')) {
    return priceList.find((p) => /koli|kargo/i.test(String(p.title))) || null;
  }
  const exact = priceList.find(
    (p) => String(p.title).trim().toLowerCase() === tn
  );
  if (exact) return exact;
  const partial = priceList.find((p) =>
    String(p.title).trim().toLowerCase().includes(tn)
  );
  return partial || null;
};

export default function HandshakesReportPage() {
  const { language } = useLocalization();
  const navigate = useNavigate();
  const mallID = localStorage.getItem('mallID');
  const branchID = localStorage.getItem('branchID');
  const parsedUser = JSON.parse(localStorage.getItem('user'));
  const [handshakeList, setHandshakeList] = useState([]);
  const [allData, setAllData] = useState([]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [filterModal, setFilterModal] = useState(false);
  const [handshakeNo, setHandshakeNo] = useState('');
  const [requestNo, setRequestNo] = useState('');
  const [store, setStore] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [person, setPerson] = useState('');
  const [qrType, setQrType] = useState('');
  const [accepting, setAccepting] = useState('');
  const [direction, setDirection] = useState('');

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [avmOverflowModal, setAvmOverflowModal] = useState(false);

  const [selectedProjectBranches, setSelectedProjectBranches] = useState([]);
  const [projectSearch, setProjectSearch] = useState('');

  const userCategory = useMemo(
    () => tokenService?.getUser()?.accountCategory,
    []
  );

  const isMallUser = userCategory !== 'company' && userCategory !== 'store';

  const projectBranchOptions = useMemo(() => {
    if (isMallUser) {
      const malls = parsedUser?.authLocations?.mall || [];
      return malls
        .map((m) => ({ branchID: m.id, mallTitle: m.title }))
        .sort((a, b) => a.mallTitle.localeCompare(b.mallTitle, 'tr'));
    }
    const branches = parsedUser?.authLocations?.branch || [];
    return branches
      .filter((b) => b.mall && b.mall.length > 0)
      .map((b) => ({ branchID: b.branchID, mallTitle: b.mall[0].title }))
      .sort((a, b) => a.mallTitle.localeCompare(b.mallTitle, 'tr'));
  }, [parsedUser, isMallUser]);

  const abortRef = useRef(null);

  const handleRequestNoClick = (handshakeNo, documentNo) => {
    if (handshakeNo && documentNo) {
      const cleanDocumentNo = documentNo.replace(/\s*\([^)]*\)\s*$/, '').trim();
      const url = `/dashboard/handshake-detail/${handshakeNo}/${encodeURIComponent(cleanDocumentNo)}`;
      window.open(url, '_blank');
    }
  };

  const handleDetailClick = async (handshakeID) => {
    setIsLoading(true);
    try {
      const response = await Services.GetIrsaliyeDetail(handshakeID);

      if (
        response.data &&
        response.data.result &&
        response.data.result.length > 0
      ) {
        const results = response.data.result;
        const mainData = results[0];

        let directionText = 'Bilinmiyor';
        let isPorterSender = false;

        if (mainData.direction === 'StoreToMallLogistics') {
          directionText = 'Mağazadan\nMal Kabule';
          isPorterSender = false;
        } else if (mainData.direction === 'MallLogisticsToStore') {
          directionText = 'Mal Kabulden\nMağazaya';
          isPorterSender = true;
        } else if (mainData.direction === 'StoreToStore') {
          if (mainData.fromLocationType === 'warehouse') {
            directionText = 'Depodan\nMağazaya';
            isPorterSender = true;
          } else {
            directionText = 'Mağazadan\nDepoya';
            isPorterSender = false;
          }
        }

        const deliveryMethod = mainData.handover?.message ? 'SMS' : '-';
        const qrType =
          mainData.confirmationType === 'static-qr'
            ? 'Statik QR'
            : 'Dinamik QR';

        const storePersonNameRaw = mainData.handover
          ? {
              name: mainData.handover.name,
              lastname: mainData.handover.lastname,
            }
          : null;

        const storePersonName = storePersonNameRaw
          ? `${maskNamePart(storePersonNameRaw.name)} ${maskNamePart(
              storePersonNameRaw.lastname
            )}`.trim()
          : mainData.branchTitle;

        const storeEmployeeInfo = {
          name: `${storePersonName} (Mağaza Çalışanı)`,
          date: formatters.toDateTimeString(mainData.handover?.createdDateTime),
        };

        const porterFull = `${mainData.porterName || ''} ${
          mainData.porterLastname || ''
        }`.trim();
        const porterRaw = {
          name: mainData.porterName,
          lastname: mainData.porterLastname,
        };
        const porterMasked =
          porterRaw.name || porterRaw.lastname
            ? `${maskNamePart(porterRaw.name)} ${maskNamePart(
                porterRaw.lastname
              )}`.trim()
            : porterFull;

        const logisticsEmployeeInfo = {
          name: `${porterMasked} (Taşıma Personeli)`,
          date: formatters.toDateTimeString(
            mainData.handshakeConfirmedDateTime
          ),
        };

        let receiverData = {};
        let senderData = {};

        if (isPorterSender) {
          senderData = logisticsEmployeeInfo;
          receiverData = storeEmployeeInfo;
        } else {
          senderData = storeEmployeeInfo;
          receiverData = logisticsEmployeeInfo;
        }

        const collectedBarcodes =
          results.length > 1
            ? results.map((item) => item.barcode).filter(Boolean)
            : [];

        const priceList =
          getPriceListForMall(mainData.mallTitle).length > 0
            ? getPriceListForMall(mainData.mallTitle)
            : mainData.transportationToolWithPrice || [];
        const totalPackageCount = results.reduce(
          (sum, item) => sum + (Number(item.requestedInventoryCount) || 0),
          0
        );

        let totalOverall = 0;
        const servicesArray = mainData.details
          ? mainData.details.map((d) => {
              const toolNameLower = String(d.transportationTool)
                .trim()
                .toLowerCase();
              const isKoliKargo =
                toolNameLower === 'ring' || /koli|kargo/i.test(toolNameLower);
              const rawCount = Number(d.transportationToolCount) || 0;
              const count =
                isKoliKargo && totalPackageCount > 0
                  ? totalPackageCount
                  : rawCount;
              const priceEntry = findPriceEntry(
                priceList,
                d.transportationTool
              );
              const unitPriceNum = priceEntry
                ? parsePriceString(priceEntry.price)
                : 0;
              const totalNum = unitPriceNum * count;
              totalOverall += totalNum;

              return {
                name: isKoliKargo ? 'Koli / Kargo' : d.transportationTool,
                qrType: qrType,
                method: deliveryMethod,
                amount: count,
                unitPrice: formatPriceString(unitPriceNum),
                totalPrice: formatPriceString(totalNum),
                currency:
                  priceEntry?.currency || priceList[0]?.currency || 'TRY',
              };
            })
          : [];

        const formattedData = {
          date: formatters.toDaTeString(mainData.handshakeConfirmedDateTime),
          time: formatters.toTimeString(mainData.handshakeConfirmedDateTime),
          deliveryDirection: directionText,
          mallName: mainData.mallTitle,
          storeName: mainData.branchTitle,
          waybillNo: handshakeID,
          companyTitle: mainData.companyLegalTitle,
          vkn: mainData.billing?.taxNo || '',
          services: servicesArray,
          totalPrice: formatPriceString(totalOverall),
          currency: priceList[0]?.currency || 'TRY',
          transportItems: results.map((item) => {
            let rowLabel = 'KARGO\nDETAY';
            if (item.direction === 'StoreToMallLogistics') {
              rowLabel = 'KARGO\nALIM';
            } else if (item.direction === 'MallLogisticsToStore') {
              rowLabel = 'KARGO\nTESLİM';
            } else if (item.direction === 'StoreToStore') {
              if (item.fromLocationType === 'warehouse') {
                rowLabel = 'KARGO\nTESLİM';
              } else {
                rowLabel = 'KARGO\nALIM';
              }
            }
            return {
              typeLabel: rowLabel,
              detailText: `(${item.documentNo}) ${item.shipmentFirm} x${item.requestedInventoryCount}`,
            };
          }),
          barcodes: collectedBarcodes,
          receiver: receiverData,
          sender: senderData,
        };

        setDetailData(formattedData);
        setDetailModalOpen(true);
      }
    } catch (error) {
      console.error(language.commons.errors.fetch_failed);
    } finally {
      setIsLoading(false);
    }
  };

  const TABLE_HEAD = [
    { id: 'project', label: 'Proje' },
    { id: 'createdDate', label: 'Tarih' },
    { id: 'handshakeNo', label: 'İrsaliye No' },
    {
      id: 'requestNo',
      label: 'Talep No',
      format: (value, row) => {
        if (!value) return '';
        const requestNos = value.split(',').map((no) => no.trim());
        return (
          <>
            {requestNos.map((requestNo, index) => (
              <React.Fragment key={index}>
                {index > 0 && ', '}
                <Link
                  component="button"
                  variant="body2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRequestNoClick(row.handshakeNo, requestNo);
                  }}
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    color: 'primary.main',
                    '&:hover': {
                      color: 'primary.dark',
                    },
                  }}
                >
                  {requestNo}
                </Link>
              </React.Fragment>
            ))}
          </>
        );
      },
    },
    { id: 'createdTime', label: 'Saat' },
    { id: 'store', label: 'Mağaza' },
    { id: 'quantity', label: 'Sefer Sayısı' },
    { id: 'vehicle', label: 'Araç Tipi' },
    { id: 'transportStaff', label: 'Taşıma Personeli' },
    { id: 'qrType', label: 'QR Tipi' },
    { id: 'accepting', label: 'Kabul Eden' },
    {
      id: 'totalQuantity',
      label: 'Toplam Paket Adedi',
      format: (value, row) => (row.totalQuantity > 0 ? row.totalQuantity : ''),
    },
    { id: 'direction', label: 'Hareket Yönü' },
    { id: 'note', label: 'Not' },
    {
      id: 'viewDetail',
      label: 'Görüntüle',
      format: (value, row) => (
        <IconButton
          color="primary"
          onClick={(e) => {
            e.stopPropagation();
            handleDetailClick(row.handshakeNo);
          }}
          title="Detayları Görüntüle"
        >
          <DescriptionIcon sx={{ color: 'rgba(101, 101, 101, 1)' }} />
        </IconButton>
      ),
    },
    {
      id: 'amountExclVat',
      label: 'Tutar (Kdv Hariç)',
      format: (value, row) => row.amountExclVatText || '',
    },
  ];

  const getPriceListForMall = (mallTitle) => {
    if (!mallTitle) return [];
    const allowed = parsedUser?.allowedOffices;

    if (allowed) {
      const byTitle = allowed[mallTitle];
      if (byTitle?.details?.transportationToolsWithPrice) {
        return byTitle.details.transportationToolsWithPrice;
      }
      const byId = Object.values(allowed).find(
        (x) => x?.details?.id === mallTitle
      );
      return byId?.details?.transportationToolsWithPrice || [];
    }

    const branches = parsedUser?.authLocations?.branch || [];
    for (const branch of branches) {
      const mall = (branch.mall || []).find(
        (m) => m.title === mallTitle || m.id === mallTitle
      );
      if (mall?.transportationToolWithPrice?.length > 0) {
        return mall.transportationToolWithPrice;
      }
    }
    return [];
  };

  const isFetchingRef = useRef(false);

  const fetchHandshakes = async (start, end, overrideBranches) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    document.body.classList.add('loading');

    try {
      const branchesForRequest =
        overrideBranches !== undefined
          ? overrideBranches
          : selectedProjectBranches;

      const payload = isMallUser
        ? {
            start,
            end,
            mall: branchesForRequest.length > 0 ? branchesForRequest : mallID,
          }
        : {
            start,
            end,
            branch:
              branchesForRequest.length > 0 ? branchesForRequest : branchID,
          };

      const response = await api.post('flow/get-handshake-report', payload, {
        signal: controller.signal,
      });

      if (response?.data?.status) {
        const data = response.data.result;

        if (typeof data === 'string' && data.includes('Kayıt bulunamadı')) {
          setHandshakeList([]);
          setAllData([]);
        } else {
          const formattedData = (Array.isArray(data) ? data : []).map(
            (item) => {
              const rawVehicle = item[7];
              const vehicleName = /koli|kargo|ring/i.test(String(rawVehicle))
                ? 'Koli / Kargo'
                : rawVehicle;
              const quantityNum = Number(item[6]) || 0;
              const totalQuantityNum = item[11] != null ? Number(item[11]) : 0;

              const rowPriceList = getPriceListForMall(item[0]);
              const priceEntry = findPriceEntry(rowPriceList, vehicleName);
              const unitPrice = parsePriceString(priceEntry?.price);

              const isKoliKargo = /koli|kargo|ring/i.test(String(vehicleName));
              const multiplier = isKoliKargo ? totalQuantityNum : quantityNum;

              const amountExclVat = unitPrice * multiplier;

              return {
                project: item[0],
                createdDate: item[1]
                  ? moment(item[1], 'MM-DD-YYYY').format('DD/MM/YYYY')
                  : '',
                handshakeNo: item[2],
                requestNo: item[3],
                createdTime: item[4],
                store: item[5],

                quantity: quantityNum,
                vehicle: vehicleName,
                transportStaff: item[8],
                qrType: item[9],
                accepting: item[10],
                totalQuantity: totalQuantityNum,
                direction: item[12],
                note: item[13],

                amountExclVat,
                amountExclVatText: `${formatPriceString(amountExclVat)} ₺`,
              };
            }
          );

          setHandshakeList(formattedData);
          setAllData(formattedData);
        }
      } else {
        console.error(
          language?.commons?.errors?.fetch_failed || 'Fetch failed',
          response?.data?.message
        );
        setHandshakeList([]);
        setAllData([]);
      }
    } catch (error) {
      const aborted =
        error?.name === 'CanceledError' ||
        error?.code === 'ERR_CANCELED' ||
        controller.signal.aborted;

      if (!aborted) {
        console.error(language.commons.errors.fetch_failed, error);
        setHandshakeList([]);
        setAllData([]);
      }
    } finally {
      isFetchingRef.current = false;
      if (abortRef.current === controller) {
        setIsLoading(false);
        document.body.classList.remove('loading');
        abortRef.current = null;
      }
    }
  };

  const exportToExcel = () => {
    const tempData = handshakeList?.map((x, index) => ({
      Proje: x.project || '',
      Tarih: x.createdDate || '',
      'İrsaliye No': x.handshakeNo || '',
      'Talep No': x.requestNo || '',
      Saat: x.createdTime || '',
      Mağaza: x.store || '',
      'Sefer Sayısı': Number(x.quantity) || 0,
      'Araç Tipi': x.vehicle || '',
      'Taşıma Personeli': x.transportStaff || '',
      'QR Tipi': x.qrType || '',
      'Kabul Eden': x.accepting || '',
      'Toplam Paket Adedi': Number(x.totalQuantity) || 0,
      'Hareket Yönü': x.direction || '',
      Not: x.note || '',
      'Tutar (KDV Hariç)': Number(x.amountExclVat) || 0,
    }));

    api
      .post('excel/export', {
        type: 'excel',
        content: tempData,
        header: {
          Proje: 'string',
          Tarih: 'string',
          'İrsaliye No': 'string',
          'Talep No': 'string',
          Saat: 'string',
          Mağaza: 'string',
          'Sefer Sayısı': 'number',
          'Araç Tipi': 'string',
          'Taşıma Personeli': 'string',
          'QR Tipi': 'string',
          'Kabul Eden': 'string',
          'Toplam Paket Adedi': 'number',
          'Hareket Yönü': 'string',
          Not: 'string',
          'Tutar (KDV Hariç)': 'number',
        },
        sheetTitle: 'İrsaliye Raporu',
      })
      .then((response) => {
        if (response.data.status) {
          const newWindow = window.open(response.data.result, '_blank');
          if (
            !newWindow ||
            newWindow.closed ||
            typeof newWindow.closed === 'undefined'
          ) {
            alert(
              'Pop-up engellendi. Lütfen pop-up engelleyicisini devre dışı bırakın.'
            );
          }
        } else {
          console.error('EXCEL aktarma hatası:', response.data.message);
        }
      })
      .catch((error) => {
        console.error("EXCEL'e aktarılırken bir hata meydana geldi:", error);
      });
  };

  const vehicleSummaryItems = useMemo(() => {
    const list = Array.isArray(handshakeList) ? handshakeList : [];

    const totals = list.reduce((acc, row) => {
      const vehicleRaw = row?.vehicle;
      const key = String(vehicleRaw ?? '').trim() || 'Bilinmeyen';

      const isKoliKargo = /koli|kargo|ring/i.test(key);
      const normalizedKey = isKoliKargo ? 'Koli / Kargo' : key;
      const rawQty = isKoliKargo
        ? Number(row?.totalQuantity)
        : Number(row?.quantity);
      const safeQty = Number.isFinite(rawQty) ? rawQty : 0;

      acc[normalizedKey] = (acc[normalizedKey] || 0) + safeQty;
      return acc;
    }, {});

    return Object.entries(totals)
      .sort((a, b) => {
        const diff = (b[1] || 0) - (a[1] || 0);
        if (diff !== 0) return diff;
        return String(a[0]).localeCompare(String(b[0]), 'tr');
      })
      .map(([label, value]) => ({
        key: label,
        label,
        value,
      }));
  }, [handshakeList]);

  const totalAmountExclVat = useMemo(() => {
    const list = Array.isArray(handshakeList) ? handshakeList : [];
    return list.reduce((sum, r) => sum + (Number(r.amountExclVat) || 0), 0);
  }, [handshakeList]);

  const LeftTotalAmount = useMemo(() => {
    const txt = `${formatPriceString(totalAmountExclVat)} ₺ (KDV Hariç)`;
    return (
      <Typography sx={{ fontWeight: 600, color: '#374151' }}>
        Toplam Tutar: {txt}
      </Typography>
    );
  }, [totalAmountExclVat]);

  const handleFilter = () => {
    if (startDate && endDate) {
      const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
      const formattedEndDate = moment(endDate).format('YYYY-MM-DD');
      fetchHandshakes(formattedStartDate, formattedEndDate);
    }
  };

  const clearFilters = () => {
    setHandshakeNo('');
    setRequestNo('');
    setStore('');
    setVehicle('');
    setPerson('');
    setQrType('');
    setAccepting('');
    setDirection('');
    setSelectedProjectBranches([]);
    setHandshakeList(allData);
  };

  const handleFilterByFields = (overrides = {}) => {
    const hn =
      overrides.handshakeNo !== undefined ? overrides.handshakeNo : handshakeNo;
    const rn =
      overrides.requestNo !== undefined ? overrides.requestNo : requestNo;
    const st = overrides.store !== undefined ? overrides.store : store;
    const vh = overrides.vehicle !== undefined ? overrides.vehicle : vehicle;
    const pr = overrides.person !== undefined ? overrides.person : person;
    const qt = overrides.qrType !== undefined ? overrides.qrType : qrType;
    const ac =
      overrides.accepting !== undefined ? overrides.accepting : accepting;
    const dir =
      overrides.direction !== undefined ? overrides.direction : direction;

    let filteredData = allData;

    if (hn) {
      filteredData = filteredData?.filter((item) =>
        item.handshakeNo?.toLocaleLowerCase().includes(hn.toLocaleLowerCase())
      );
    }
    if (rn) {
      filteredData = filteredData?.filter((item) =>
        item.requestNo?.toLocaleLowerCase().includes(rn.toLocaleLowerCase())
      );
    }
    if (st) {
      filteredData = filteredData?.filter((item) =>
        item.store?.toLocaleLowerCase()?.includes(st.toLocaleLowerCase())
      );
    }
    if (vh) {
      filteredData = filteredData.filter((item) =>
        item.vehicle?.toLocaleLowerCase().includes(vh.toLocaleLowerCase())
      );
    }
    if (pr) {
      filteredData = filteredData.filter((item) =>
        item.transportStaff
          ?.toLocaleLowerCase()
          .includes(pr.toLocaleLowerCase())
      );
    }
    if (qt) {
      filteredData = filteredData.filter((item) =>
        item.qrType?.toLocaleLowerCase().includes(qt.toLocaleLowerCase())
      );
    }
    if (ac) {
      filteredData = filteredData.filter((item) =>
        item.accepting?.toLocaleLowerCase().includes(ac.toLocaleLowerCase())
      );
    }
    if (dir) {
      filteredData = filteredData.filter((item) =>
        item.direction?.toLocaleLowerCase().includes(dir.toLocaleLowerCase())
      );
    }

    setHandshakeList(filteredData);
  };

  useEffect(() => {
    const defaultEndMoment = moment();
    const defaultStartMoment = moment().subtract(7, 'days');

    setStartDate(defaultStartMoment);
    setEndDate(defaultEndMoment);

    fetchHandshakes(
      defaultStartMoment.format('YYYY-MM-DD'),
      defaultEndMoment.format('YYYY-MM-DD')
    );

    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      document.body.classList.remove('loading');
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>İrsaliye Raporu | Mall Logistics Panel</title>
      </Helmet>

      <Stack style={{ paddingInline: '25px' }} spacing={3}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h4" gutterBottom>
            İrsaliye Raporu
          </Typography>
          {handshakeList?.length > 0 && (
            <Button variant="contained" color="primary" onClick={exportToExcel}>
              Excel'e Aktar
            </Button>
          )}
        </Stack>

        <Card>
          <DynamicTable
            data={handshakeList}
            headers={TABLE_HEAD}
            initialOrder="Tarih"
            initalOrderDirection="desc"
          >
            <Stack direction="column" sx={{ gap: 0.75 }}>
              {/* Satır 1: Filtreler + Toplam Tutar + AVM chips */}
              <Stack
                direction="row"
                alignItems="center"
                sx={{ flexWrap: 'wrap', gap: 1 }}
              >
                <FilterViewButton
                  variant="contained"
                  onClick={() => setFilterModal(true)}
                />
                {LeftTotalAmount}
                {selectedProjectBranches.length > 0 && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ flexWrap: 'wrap', gap: 0.5 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: 'text.secondary',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      AVM Filtreleri:
                    </Typography>
                    {selectedProjectBranches
                      .slice(0, MAX_VISIBLE_CHIPS)
                      .map((bid) => {
                        const opt = projectBranchOptions.find(
                          (o) => o.branchID === bid
                        );
                        return (
                          <Chip
                            key={bid}
                            label={opt?.mallTitle || bid}
                            onDelete={() => {
                              const updated = selectedProjectBranches.filter(
                                (id) => id !== bid
                              );
                              setSelectedProjectBranches(updated);
                              if (startDate && endDate) {
                                fetchHandshakes(
                                  moment(startDate).format('YYYY-MM-DD'),
                                  moment(endDate).format('YYYY-MM-DD'),
                                  updated
                                );
                              }
                            }}
                            size="small"
                            variant="outlined"
                          />
                        );
                      })}
                    {selectedProjectBranches.length > MAX_VISIBLE_CHIPS && (
                      <Chip
                        label={`+${
                          selectedProjectBranches.length - MAX_VISIBLE_CHIPS
                        } AVM`}
                        size="small"
                        variant="outlined"
                        onClick={() => setAvmOverflowModal(true)}
                        sx={{ cursor: 'pointer' }}
                      />
                    )}
                  </Stack>
                )}
              </Stack>

              {/* Satır 2: Araç chips + Mağaza Adı (sadece içerik varsa göster) */}
              {(vehicleSummaryItems.length > 0 || store) && (
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{ flexWrap: 'wrap', gap: 1 }}
                >
                  {vehicleSummaryItems.length > 0 && (
                    <RightHeaderSummary items={vehicleSummaryItems} />
                  )}
                  {store && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      sx={{ gap: 0.5 }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: 'text.secondary',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Mağaza Adı:
                      </Typography>
                      <Chip
                        label={store}
                        onDelete={() => {
                          setStore('');
                          handleFilterByFields({ store: '' });
                        }}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>
                  )}
                </Stack>
              )}
            </Stack>
          </DynamicTable>
        </Card>

        <CustomFilter
          modalType="drawer"
          filterModal={filterModal}
          handleCloseModal={() => setFilterModal(false)}
        >
          <Stack direction="column" spacing={2} p={2}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                format="DD/MM/YYYY"
                label={language.commons.filter.start_date}
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                format="DD/MM/YYYY"
                label={language.commons.filter.end_date}
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
              />
            </LocalizationProvider>
            {projectBranchOptions.length > 1 && (
              <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                <Select
                  multiple
                  displayEmpty
                  value={selectedProjectBranches}
                  onChange={(e) => {
                    const { value } = e.target;
                    setSelectedProjectBranches(
                      typeof value === 'string' ? value.split(',') : value
                    );
                  }}
                  input={<OutlinedInput />}
                  renderValue={() =>
                    selectedProjectBranches.length > 0
                      ? `Proje (${selectedProjectBranches.length})`
                      : 'Proje'
                  }
                  MenuProps={{
                    PaperProps: {
                      sx: { maxHeight: 360, borderRadius: 2, mt: 0.5 },
                    },
                    autoFocus: false,
                  }}
                  onClose={() => setProjectSearch('')}
                  sx={{
                    color:
                      selectedProjectBranches.length > 0
                        ? 'text.primary'
                        : 'text.secondary',
                  }}
                >
                  <ListSubheader
                    sx={{
                      px: 1.5,
                      pt: 1.5,
                      pb: 1,
                      lineHeight: 'unset',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Proje Seçin ({selectedProjectBranches.length})
                      </Typography>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedProjectBranches(
                            projectBranchOptions.map((o) => o.branchID)
                          );
                        }}
                        sx={{
                          cursor: 'pointer',
                          color: 'primary.main',
                          fontWeight: 500,
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        Tümünü Seç
                      </Link>
                    </Stack>
                    <TextField
                      size="small"
                      placeholder="Proje adı filtreleyin..."
                      fullWidth
                      value={projectSearch}
                      onChange={(e) => setProjectSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </ListSubheader>
                  {projectBranchOptions
                    .filter((o) =>
                      o.mallTitle
                        .toLocaleLowerCase()
                        .includes(projectSearch.toLocaleLowerCase())
                    )
                    .map((o) => {
                      const isSelected = selectedProjectBranches.includes(
                        o.branchID
                      );
                      return (
                        <MenuItem
                          key={o.branchID}
                          value={o.branchID}
                          sx={{
                            mx: 1,
                            mb: 0.5,
                            borderRadius: 1,
                            border: `1px solid ${
                              isSelected ? '#1976d2' : 'transparent'
                            }`,
                            '&.Mui-selected': {
                              backgroundColor: 'transparent',
                            },
                            '&.Mui-selected:hover': {
                              backgroundColor: 'rgba(25, 118, 210, 0.04)',
                            },
                          }}
                        >
                          <Checkbox
                            checked={isSelected}
                            size="small"
                            sx={{ p: 0.5, mr: 1 }}
                          />
                          {o.mallTitle}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
            )}
            <Stack direction="row" spacing={2}>
              <ClearButton
                onClick={() => {
                  setHandshakeList([]);
                  setSelectedProjectBranches([]);
                }}
              />
              <Button
                sx={{
                  backgroundColor: '#1570EF',
                  fontWeight: 400,
                  fontSize: 14,
                  fontFamily: 'Inter',
                  width: 97,
                  height: 40,
                  borderRadius: '5px',
                  padding: '8px 16px 8px 16px',
                  gap: '6px',
                }}
                variant="contained"
                onClick={handleFilter}
              >
                Listele
              </Button>
            </Stack>
            <Divider />
            <FormControl sx={{ flex: 1, minWidth: '100%' }}>
              <TextField
                label={'İrsaliye No'}
                variant="outlined"
                value={handshakeNo}
                onChange={(e) => setHandshakeNo(e.target.value)}
              />
            </FormControl>

            <FormControl sx={{ flex: 1, minWidth: '100%' }}>
              <TextField
                label={'Talep No'}
                variant="outlined"
                value={requestNo}
                onChange={(e) => setRequestNo(e.target.value)}
              />
            </FormControl>
            {(isMallUser || projectBranchOptions.length > 1) && (
              <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                <TextField
                  label={language.commons.store}
                  variant="outlined"
                  value={store}
                  onChange={(e) => setStore(e.target.value)}
                />
              </FormControl>
            )}
            <FormControl sx={{ flex: 1, minWidth: '100%' }}>
              <TextField
                label={language.commons.vehicle_type}
                variant="outlined"
                value={vehicle}
                onChange={(e) => setVehicle(e.target.value)}
              />
            </FormControl>
            <FormControl sx={{ flex: 1, minWidth: '100%' }}>
              <TextField
                label={language.commons.column.transport_person}
                variant="outlined"
                value={person}
                onChange={(e) => setPerson(e.target.value)}
              />
            </FormControl>
            <FormControl sx={{ flex: 1, minWidth: '100%' }}>
              <TextField
                label={'QR Tipi'}
                variant="outlined"
                value={qrType}
                onChange={(e) => setQrType(e.target.value)}
              />
            </FormControl>
            <FormControl sx={{ flex: 1, minWidth: '100%' }}>
              <TextField
                label={'Kabul Eden'}
                variant="outlined"
                value={accepting}
                onChange={(e) => setAccepting(e.target.value)}
              />
            </FormControl>
            <FormControl sx={{ flex: 1, minWidth: '100%' }}>
              <TextField
                label={language.commons.direction}
                variant="outlined"
                value={direction}
                onChange={(e) => setDirection(e.target.value)}
              />
            </FormControl>
            <Stack direction="row" spacing={2}>
              <ClearButton onClick={clearFilters} />
              <FilterButton onClick={handleFilterByFields} />
            </Stack>
          </Stack>
        </CustomFilter>

        <Modal
          open={avmOverflowModal}
          onClose={() => setAvmOverflowModal(false)}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 3,
              minWidth: 320,
              maxWidth: 480,
              maxHeight: '80vh',
              overflowY: 'auto',
              outline: 0,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={0.5}
            >
              <Typography variant="h6">AVM Filtreleri</Typography>
              <IconButton
                size="small"
                onClick={() => setAvmOverflowModal(false)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Filtrelenmiş tüm avmler ({selectedProjectBranches.length})
            </Typography>
            <Stack spacing={1}>
              {selectedProjectBranches.map((bid) => {
                const opt = projectBranchOptions.find(
                  (o) => o.branchID === bid
                );
                return (
                  <Chip
                    key={bid}
                    label={opt?.mallTitle || bid}
                    onDelete={() => {
                      const updated = selectedProjectBranches.filter(
                        (id) => id !== bid
                      );
                      setSelectedProjectBranches(updated);
                      if (updated.length === 0) setAvmOverflowModal(false);
                      if (startDate && endDate) {
                        fetchHandshakes(
                          moment(startDate).format('YYYY-MM-DD'),
                          moment(endDate).format('YYYY-MM-DD'),
                          updated
                        );
                      }
                    }}
                    size="small"
                    variant="outlined"
                    sx={{ width: '100%', justifyContent: 'space-between' }}
                  />
                );
              })}
            </Stack>
          </Box>
        </Modal>

        <Modal open={detailModalOpen} onClose={() => setDetailModalOpen(false)}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '100%',
              maxWidth: '600px',
              bgcolor: '#ffffff',
              boxShadow: 24,
              p: '18px 18px 18px 18px',
              outline: 0,
              maxHeight: '90vh',
              overflowY: 'auto',
              fontFamily: "'Inter', sans-serif",
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <CloseIcon
              size={12}
              sx={{
                cursor: 'pointer',
                alignSelf: 'flex-end',
                color: 'rgba(12, 9, 42, 1)',
              }}
              onClick={() => setDetailModalOpen(false)}
            />
            <Box sx={{ border: '1px solid rgba(227, 227, 227, 1)', p: 2 }}>
              {detailData && (
                <>
                  <Box sx={{ textAlign: 'center', mb: '40px' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#1a428a',
                        fontSize: '22px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        mb: '5px',
                      }}
                    >
                      İRSALİYE TAKİP BELGESİ
                    </Typography>
                    <Typography
                      sx={{ color: '#666', fontSize: '14px', fontWeight: 400 }}
                    >
                      Kargo ve Teslimat Bilgi Formu
                    </Typography>
                  </Box>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{
                      mb: '30px',
                      fontSize: '11px',
                      lineHeight: 1.2,
                      color: '#333',
                    }}
                  >
                    <Box sx={{ width: '45%' }}>
                      <Box
                        component="img"
                        src="/assets/blueLogo.png"
                        alt="Mall Logistics"
                        sx={{ maxWidth: '150px', display: 'block', mb: '15px' }}
                      />
                      <Box sx={{ mb: '3px' }}>
                        <span
                          style={{
                            fontWeight: 700,
                            marginRight: '5px',
                            color: '#222',
                          }}
                        >
                          Tarih:
                        </span>
                        <span style={{ color: '#333' }}>{detailData.date}</span>
                      </Box>
                      <Box sx={{ mb: '3px' }}>
                        <span
                          style={{
                            fontWeight: 700,
                            marginRight: '5px',
                            color: '#222',
                          }}
                        >
                          Saat:
                        </span>
                        <span style={{ color: '#333' }}>{detailData.time}</span>
                      </Box>
                      <Box sx={{ mb: '3px' }}>
                        <span
                          style={{
                            fontWeight: 700,
                            marginRight: '5px',
                            color: '#222',
                          }}
                        >
                          Teslim Yönü:
                        </span>
                        <span style={{ color: '#333', whiteSpace: 'pre-line' }}>
                          {detailData.deliveryDirection}
                        </span>
                      </Box>
                    </Box>

                    <Box sx={{ width: '50%' }}>
                      <Box sx={{ mb: '3px' }}>
                        <span
                          style={{
                            fontWeight: 700,
                            marginRight: '5px',
                            color: '#222',
                          }}
                        >
                          AVM Adı:
                        </span>
                        <span style={{ color: '#333' }}>
                          {detailData.mallName}
                        </span>
                      </Box>
                      <Box sx={{ mb: '3px' }}>
                        <span
                          style={{
                            fontWeight: 700,
                            marginRight: '5px',
                            color: '#222',
                          }}
                        >
                          Mağaza Adı:
                        </span>
                        <span style={{ color: '#333' }}>
                          {detailData.storeName}
                        </span>
                      </Box>
                      <Box sx={{ mb: '3px' }}>
                        <span
                          style={{
                            fontWeight: 700,
                            marginRight: '5px',
                            color: '#222',
                          }}
                        >
                          İrsaliye No:
                        </span>
                        <span style={{ color: '#333' }}>
                          {detailData.waybillNo}
                        </span>
                      </Box>
                      <Box sx={{ mb: '3px' }}>
                        <span
                          style={{
                            fontWeight: 700,
                            marginRight: '5px',
                            color: '#222',
                          }}
                        >
                          Ticari Ünvanı:
                        </span>
                        <span style={{ color: '#333', whiteSpace: 'pre-line' }}>
                          {detailData.companyTitle}
                        </span>
                      </Box>
                      <Box sx={{ mb: '3px' }}>
                        <span
                          style={{
                            fontWeight: 700,
                            marginRight: '5px',
                            color: '#222',
                          }}
                        >
                          VKN:
                        </span>
                        <span style={{ color: '#333' }}>{detailData.vkn}</span>
                      </Box>
                    </Box>
                  </Stack>

                  <TableContainer sx={{ mb: '10px' }}>
                    <Table
                      sx={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        '& th, & td': {
                          border: '1px solid #777',
                          padding: '10px 5px',
                          textAlign: 'center',
                          verticalAlign: 'middle',
                          fontSize: '13px',
                          color: '#444',
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow
                          sx={{
                            '& th': {
                              bgcolor: '#eef1f6',
                              fontWeight: 700,
                              color: '#444',
                            },
                          }}
                        >
                          <TableCell sx={{ width: '60px' }}>&nbsp;</TableCell>
                          <TableCell sx={{ width: '120px' }}>Hizmet</TableCell>
                          <TableCell sx={{ width: '80px' }}>
                            Teslim Yöntemi
                          </TableCell>
                          <TableCell sx={{ width: '80px' }}>Miktar</TableCell>
                          <TableCell sx={{ width: '80px' }}>
                            Birim Fiyat
                          </TableCell>
                          <TableCell sx={{ width: '80px' }}>Tutar</TableCell>
                          <TableCell sx={{ width: '80px' }}>
                            Para Birimi
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detailData.services.map((service, index) => (
                          <TableRow key={index}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>{service.name}</TableCell>
                            <TableCell>{service.qrType}</TableCell>
                            <TableCell>{service.amount}</TableCell>
                            <TableCell>{service.unitPrice ?? '-'}</TableCell>
                            <TableCell>{service.totalPrice ?? '-'}</TableCell>
                            <TableCell>
                              {service.currency ?? detailData.currency}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell>&nbsp;</TableCell>
                          <TableCell
                            colSpan={3}
                            sx={{
                              textAlign: 'left',
                              fontWeight: 700,
                              paddingLeft: '18px',
                            }}
                          >
                            Toplam
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}></TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            {detailData.totalPrice ?? '-'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            {detailData.currency ?? '-'}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TableContainer sx={{ mb: '10px' }}>
                    <Table
                      sx={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        '& th, & td': {
                          border: '1px solid #777',
                          padding: '10px 5px',
                          textAlign: 'center',
                          verticalAlign: 'middle',
                          fontSize: '13px',
                          color: '#444',
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow
                          sx={{
                            '& th': {
                              bgcolor: '#eef1f6',
                              fontWeight: 700,
                              color: '#444',
                            },
                          }}
                        >
                          <TableCell sx={{ width: 'auto' }}>
                            Taşıma Detayı
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detailData.transportItems.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>{item.detailText}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {detailData.barcodes && detailData.barcodes.length > 0 && (
                    <Box sx={{ mt: '15px', color: '#333' }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: '12px',
                          mb: '5px',
                          color: '#444',
                        }}
                      >
                        Barkod Kodları:
                      </Typography>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        sx={{
                          fontSize: '12px',
                          color: '#555',
                          lineHeight: 1.4,
                        }}
                      >
                        <Stack direction="column">
                          {detailData.barcodes
                            .slice(0, Math.ceil(detailData.barcodes.length / 2))
                            .map((code, i) => (
                              <span key={i}>{code}</span>
                            ))}
                        </Stack>
                        <Stack direction="column">
                          {detailData.barcodes
                            .slice(Math.ceil(detailData.barcodes.length / 2))
                            .map((code, i) => (
                              <span key={i}>{code}</span>
                            ))}
                        </Stack>
                      </Stack>
                    </Box>
                  )}

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{
                      mt:
                        detailData.barcodes && detailData.barcodes.length > 0
                          ? '20px'
                          : '40px',
                    }}
                  >
                    <Box sx={{ textAlign: 'center', width: '210px' }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: '15px',
                          color: '#333',
                          mb: '10px',
                          borderBottom: '1px solid #ddd',
                          pb: '10px',
                          display: 'block',
                        }}
                      >
                        Teslim Alan
                      </Typography>
                      <Typography
                        sx={{ fontSize: '12px', color: '#666', mb: '3px' }}
                      >
                        {detailData.receiver.name}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: '#888' }}>
                        {detailData.receiver.date}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', width: '210px' }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: '15px',
                          color: '#333',
                          mb: '10px',
                          borderBottom: '1px solid #ddd',
                          pb: '10px',
                          display: 'block',
                        }}
                      >
                        Teslim Eden
                      </Typography>
                      <Typography
                        sx={{ fontSize: '12px', color: '#666', mb: '3px' }}
                      >
                        {detailData.sender.name}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: '#888' }}>
                        {detailData.sender.date}
                      </Typography>
                    </Box>
                  </Stack>
                </>
              )}
            </Box>
          </Box>
        </Modal>
      </Stack>
    </>
  );
}
